import type { Fund, FundingRequest } from '../types';

// Redi bucks (Oct 2026): simulated money. Every RB sits in a named fund or on
// a named bundle, and nothing here adds funds together: Glenn's rule is that
// the money page is a pipeline, not a pot.

export function formatRb(n: number): string {
  return `RB ${n.toLocaleString('en-US')}`;
}

/** What a fund can still back. Never negative, even on a fund edited down. */
export function fundAvailable(fund: Pick<Fund, 'amount' | 'committed' | 'paid'>): number {
  return Math.max(0, fund.amount - fund.committed - fund.paid);
}

/** A request that holds money, or a place in line for it. */
export function isLiveFundingRequest(request: Pick<FundingRequest, 'status'>): boolean {
  return (
    request.status === 'requested' || request.status === 'committed' || request.status === 'paid'
  );
}

/**
 * Bundles to keep through pruneEmptyBundles because funding points at them.
 * OR it with the events and bids sets at every call.
 */
export function bundleIdsKeptByFunding(
  requests: Pick<FundingRequest, 'bundleId' | 'status'>[]
): Set<string> {
  return new Set(requests.filter(isLiveFundingRequest).map((r) => r.bundleId));
}

const REQUEST_GROUP: Record<FundingRequest['status'], number> = {
  requested: 0,
  committed: 1,
  paid: 2,
  declined: 3,
  withdrawn: 3,
  cancelled: 3,
};

/** Waiting first (oldest first, it has waited longest), then funded, paid, closed. */
export function sortFundingRequests(requests: FundingRequest[]): FundingRequest[] {
  return [...requests].sort((a, b) => {
    const group = REQUEST_GROUP[a.status] - REQUEST_GROUP[b.status];
    if (group !== 0) return group;
    const age = a.requestedAt.toMillis() - b.requestedAt.toMillis();
    return a.status === 'requested' ? age : -age;
  });
}

/** The owner's money line for a bundle: "Funded RB 12,000". Null with nothing to say. */
export function bundleFundingText(requests: FundingRequest[]): string | null {
  const parts = requests.filter(isLiveFundingRequest).map((r) => {
    const verb =
      r.status === 'paid' ? 'Paid' : r.status === 'committed' ? 'Funded' : 'Funding requested';
    return `${verb} ${formatRb(r.amount)}`;
  });
  return parts.length > 0 ? parts.join(' · ') : null;
}
